import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { analyzeFoodImage } from '../services/googleVision';
import './PhotoUpload.css';

function PhotoUpload({ dishId, userId, onUploadComplete, suggestedDishId = null }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setError(null);
      setAiAnalysis(null);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Analyze image with Google Vision AI
      analyzeImageWithAI(file);
    }
  };

  const analyzeImageWithAI = async (file) => {
    setAnalyzing(true);
    try {
      const analysis = await analyzeFoodImage(file);
      if (analysis.success) {
        setAiAnalysis(analysis);
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      // Don't show error to user, just continue without AI tags
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !userId) {
      setError('Please select an image and make sure you are logged in');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const storageRef = ref(storage, `dish-photos/${dishId}/${Date.now()}_${selectedFile.name}`);
      
      // Upload to Firebase Storage
      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);

      // Save photo metadata to Firestore with AI analysis
      await addDoc(collection(db, 'dishPhotos'), {
        dishId: dishId || aiAnalysis?.suggestedDishId,
        userId: userId,
        imageUrl: downloadURL,
        createdAt: new Date(),
        approved: false, // For moderation
        aiAnalysis: aiAnalysis ? {
          detectedDishType: aiAnalysis.detectedDishType,
          detectedCuisine: aiAnalysis.detectedCuisine,
          confidence: aiAnalysis.confidence,
          labels: aiAnalysis.labels
        } : null
      });

      // Reset form
      setSelectedFile(null);
      setPreview(null);
      
      if (onUploadComplete) {
        onUploadComplete();
      }

      alert('Photo uploaded successfully! It will appear after moderation.');
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
  };

  return (
    <div className="photo-upload-container">
      <h3>Upload Your Photo</h3>
      <p className="upload-description">
        Share your own photo of this dish with the community!
      </p>

      {!preview ? (
        <div className="upload-area">
          <label htmlFor="photo-upload" className="upload-button">
            <span className="upload-icon">📷</span>
            <span>Choose Photo</span>
          </label>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <p className="upload-hint">Max size: 5MB</p>
        </div>
      ) : (
        <div className="preview-area">
          <div className="preview-image-container">
            <img src={preview} alt="Preview" className="preview-image" />
            {analyzing && (
              <div className="ai-analyzing-overlay">
                <div className="ai-spinner">🤖</div>
                <p>AI analyzing image...</p>
              </div>
            )}
          </div>
          
          {aiAnalysis && aiAnalysis.success && (
            <div className="ai-analysis-result">
              <div className="ai-badge">✨ AI Detected</div>
              {aiAnalysis.detectedDishType && (
                <p className="ai-detection">
                  <strong>Dish Type:</strong> {aiAnalysis.detectedDishType}
                </p>
              )}
              {aiAnalysis.detectedCuisine && (
                <p className="ai-detection">
                  <strong>Cuisine:</strong> {aiAnalysis.detectedCuisine}
                </p>
              )}
              <p className="ai-confidence">
                Confidence: {Math.round(aiAnalysis.confidence * 100)}%
              </p>
            </div>
          )}

          <div className="upload-actions">
            <button onClick={handleCancel} className="btn-cancel" disabled={uploading || analyzing}>
              Cancel
            </button>
            <button onClick={handleUpload} className="btn-upload" disabled={uploading || analyzing}>
              {uploading ? 'Uploading...' : analyzing ? 'Analyzing...' : 'Upload Photo'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
}

export default PhotoUpload;

